/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import { UUID } from "../Utils/Types";
import { Path } from "./DataTransformation";

// TODO: ApolloClient is receiving dates as scalars, but it is not
// doing anything to parse them. The apollo-link-scalars library should
// handle this problem so they can be referenced without parsing them.
//
// https://github.com/eturino/apollo-link-scalars
//
// This post had good guidance about how to get that working in an app:
// https://stackoverflow.com/questions/66085887/how-can-you-retrieve-a-date-field-in-apollo-client-from-a-query-as-a-date-and-no

export interface AppInfo {
  version: VersionInfo;
  configurations: AppConfigurations;
  schemaStatus: SchemaStatus;
}

export interface AppConfigurations {
  isSensitiveSecurityContext: boolean;
}

export interface VersionInfo {
  abbreviatedCommitHash: string;
  commitHash: string;
  descriptor: string;
  buildDate: string;
}

export interface ExecutedMigration {
  __typename: "ExecutedMigration";
  changeId: string;
  timestamp: string;
}

export interface NoMigrationsExecuted {
  __typename: "NoMigrationsExecuted";
  message: string;
}

export interface ErrorGettingMigrations {
  __typename: "ErrorGettingMigrations";
  errorMessage: string;
}

export type MigrationExecutionState =
  | ExecutedMigration
  | NoMigrationsExecuted
  | ErrorGettingMigrations;

export interface SchemaStatus {
  /**
   * The state of the data schema currently in the database.
   */
  lastExecutedMigration?: MigrationExecutionState;
  /**
   * The latest migration that should have been run on the data for the application to run with this version.
   */
  expectedMigrationId?: string;
}

export interface FileInfo extends Marked {
  id: UUID;
  attributes?: FileAttributes;
  predictedFileType?: FileType;
  userSelectedFileType?: FileType;
  // The folder this file belongs to, or undefined/null when the file is unfiled.
  folderId?: UUID;
}

// A user-created folder that files can be organized into.
export interface Folder {
  id: UUID;
  name: string;
  // The parent folder, or undefined/null for a top-level folder.
  parentId?: UUID;
  creationDate?: string;
  lastModifiedDate?: string;
}

// The result of a delete mutation (e.g. deleteFolder).
export interface DeleteOutcome {
  deletedCount: number;
}

export interface NewFileInfo {
  id: UUID;
  name: string;
}

export interface PdfFileInfo {
  id: UUID;
  associatedFileInfos?: UUID[];
}

export interface FileUploadManifest {
  fileInfo: NewFileInfo[];
  uploadDelayMillis?: number;
}

export interface FileAttributes {
  name: string;
  uploadDateUtc: string;
  sizeMbs: number;
}

export interface Marked {
  securityMarkings?: SecurityMarkings;
}

export interface SecurityMarkings {
  classificationLevel: ClassificationLevel;
  compartments: CompartmentMarking[];
}

export interface CompartmentMarking {
  classificationLevel: ClassificationLevel;
  agencyToCompartments: Map<string, string[]>;
}

export enum ClassificationLevel {
  UNCLASSIFIED = "U",
  CONFIDENTIAL = "C",
  SECRET = "S",
  TOP_SECRET = "TS",
}

export interface Redaction {
  __typename: "Redaction";
  message: string;
}

/**
 * The settings that configure a query's response.
 */
export interface QuerySettingsInput {
  /**
   * The settings that configure the pagination of the query response.
   */
  paging?: PageSettingsInput;

  sort?: SortSettingsInput;
}

export interface SortSettingsInput {
  fieldName?: string;

  order?: SortOrder;
}

export type SortOrder = "ASCENDING" | "DESCENDING";

/**
 * The settings that define how to paginate data.
 */
export interface PageSettingsInput {
  /**
   * The size of each page. May be between 1 and 200. Values outside of this range will be clipped to
   * the lower or upper bound.
   */
  size?: number;

  /**
   * The index of the page. This is a 1-based index; the first page is 1.
   */
  index?: number;
}

export interface FileIdFolder {
  path: string;
  fileIds: UUID[];
}

export interface SimpleMimeType {
  mimeType: string;
  extension: string;
}

export const MIME_TYPE_UNKNOWN: SimpleMimeType = {
  mimeType: "",
  extension: "*",
};

export interface NodeDescriptor {
  id: UUID;
  displayName: string;
  allPaths: Path[];
  familyClassification: TransformFamily;
  transformScope?: TransformScope;
}

export enum TransformFamily {
  PX = "PX",
  XP = "XP",
}

export enum TransformScope {
  GRANULAR = "GRANULAR",
  FULL = "FULL",
}

export type DataContainerType =
  | "PROTO_FILE"
  | "PROTO_MESSAGE"
  | "PROTO_FIELD"
  | "XML_DOCUMENT"
  | "XML_ELEMENT"
  | "XML_ATTRIBUTE";

export type DataPrimitiveType =
  | "PROTO_INT32"
  | "PROTO_STRING"
  | "PROTO_BOOL"
  | "XML_STRING"
  | "XML_INT"
  | "XML_BOOLEAN";

export interface TransformType {
  containerType: DataContainerType;
  primitiveType?: DataPrimitiveType | null; // nullable in Kotlin, so allow null/undefined
}

// TODO: Will likely add reference to transformed file for download
export interface OutputNodeDescriptor extends NodeDescriptor {
  type: FileType;
  isTransformReady: boolean;
}

export interface ComposerNodeDescriptor extends NodeDescriptor {
  type: FileType;
  sourceInputNode?: InputNodeDescriptor;
  composerData?: Path[][];
  isTransformReady?: boolean;
  // Chosen output format for this composer (proto | xml). Optional so mappings
  // saved before this field existed still load; the UI defaults to PROTO.
  targetFormat?: FileType;
}

export enum FileType {
  UNDETECTED = "UNDETECTED",
  JAVA = "JAVA",
  C = "C",
  CPP = "CPP",
  H = "H",
  IDL = "IDL",
  PY = "PY",
  XML = "XML",
  PROTO = "PROTO",
  PB2 = "PB2",
  JS = "JS",
}

export function getFileMimeType(extension: string): string {
  let mimeType;
  switch (extension) {
    case "xml":
      mimeType = "application/xml";
      break;
    case "js":
      mimeType = "application/javascript";
      break;
    case "py":
      mimeType = "text/x-python";
      break;
    case "java":
      mimeType = "text/x-java-source";
      break;
    case "c":
      mimeType = "text/x-c";
      break;
    case "cpp":
      mimeType = "text/x-c++src";
      break;
    case "h":
      mimeType = "text/x-c";
      break;
    case "idl":
      mimeType = "text/plain";
      break;
    case "proto":
      mimeType = "text/plain";
      break;
    case "pb2":
      mimeType = "text/plain";
      break;
    default:
      mimeType = "text/plain";
  }

  return mimeType;
}

export enum LogLevel {
  INFORMATION = "INFORMATION",
  WARNING = "WARNING",
  ERROR = "ERROR",
}

export enum Component {
  FILE_UPLOAD = "file-upload",
  TRANSFORM_ENGINE = "transform-engine",
  QUICKFORM = "quickform",
  INPUTS = "inputs",
  FILES = "files",
}

export interface EventLog {
  level: LogLevel;
  component: string;
  message: string;
  timeStamp: string; // ISO date string from backend
}

export interface EventLogResponse {
  logs: EventLog[];
}

export interface InputNodeDescriptor extends NodeDescriptor {
  associatedFileId: UUID;
  storedFileId: UUID;
  type: FileType;
}

export interface TransformNodeDescriptor extends NodeDescriptor {
  familyType: FamilyType;
  transformScope: TransformScope;
}

export enum FamilyType {
  PX = "PX",
  XP = "XP",
}

export interface TransformRecommendation {
  projectId: UUID;
  transformFamilies: FileType[];
  transformIds: string[];
}

export enum ScanResult {
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
  FILETYPE_NOT_SUPPORTED = "FILETYPE_NOT_SUPPORTED",
  FILE_DOES_NOT_EXIST = "FILE_DOES_NOT_EXIST",
}

export interface LoginResponse {
  authToken: string;
  user: AuthenticatedUser;
  errors: string[];
}

export interface JwtRefreshResponse {
  authToken: string;
  errors: string[];
}

export interface ProtectedUrl {
  urlFragment: string;
}

export interface Name {
  firstName: string;
  lastName: string;
  displayName: string;
}

export interface ContactInfo {
  email: string;
}

export interface AuthenticatedUser {
  id: UUID;
  username: string;
  email?: string;
  person: Person;
  isAdmin: boolean;
  preferences?: UserPreferences;
  securityInfo: SecurityInfo;
  mustChangePassword: boolean;
}

export interface User {
  id: UUID;
  username: string;
  email?: string;
  person: Person;
  preferences?: UserPreferences;
}

export interface UserProfileInput {
  id: UUID;
  person: Person;
  preferences?: UserPreferences;
}

export interface UserPreferences {
  topicsOfInterest?: string[];
}

export interface SecurityInfo {
  clearanceLevel: ClassificationLevel;
  agencyToCompartments: Map<string, string[]>;
}

export interface Person {
  name: Name;
  contactInfo: ContactInfo;
}

export interface AppInitializationStatus {
  initialized: boolean;
}

export interface AppInitializationResponse {
  message: string;
  status: AppInitializationStatus;
}

export interface GeneralResponse {
  status: number;
  message: string;
  payload: any;
}

export interface LoginRequest {
  username: string;
  password?: string;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface FileUploadResponse {
  successfulUploads: string[];
  duplicateUploads: string[];
  failedUploads: FileError[];
}

// TODO: Can this be used instead of ElementUploadFailure or vice versa?
export interface FileError {
  fileName: string;
  uploadError: string;
}

export interface ElementUploadFailure {
  uploadedFileName: string;
  failureMessage: string;
}

export interface FileInfoInput extends Marked {
  id: UUID;
  predictedFileType: FileType;
  userSelectedFileType: FileType;
  name?: string;
  folderId?: string;
}

export type ParseFileResult = {
  fileId: string;
  hasPaths: boolean;
  error?: string;
};

export interface PickListValueOption {
  value: string;
  description?: string | null;
}

// TODO: Create PickListSchemaInput interface
export interface PickListSchema {
  id: UUID;
  attributeName: string;
  valueOptions: PickListValueOption[] | null;
}

export interface SchemaFields {
  selectMultiple: PickListSchema[] | null;
}

export type FileSize = "SMALL" | "MEDIUM" | "LARGE" | "EXTRA_LARGE";

export type ChunkType =
  | "NONE"
  | "MULTIPLICITY"
  | "REPETITION"
  | "MAP_GROUP"
  | "LIST_GROUP"
  | "ITEM";

export interface Chunk<T> {
  type: ChunkType;
  chunkList: Chunk<T>[];
  chunkMap: Map<string, Chunk<T>>;
  data?: T;
  count: number;
}
